"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type PromptCategory =
  | "creative"
  | "narrative"
  | "persuasive"
  | "descriptive"
  | "journal";

const CATEGORY_VALUES: PromptCategory[] = [
  "creative",
  "narrative",
  "persuasive",
  "descriptive",
  "journal",
];

const MAX_RECENT = 5;

function pickPrompt(list: readonly string[], avoid: string): string {
  let index = Math.floor(Math.random() * list.length);
  if (list[index] === avoid && list.length > 1) {
    index = (index + 1) % list.length;
  }
  return list[index];
}

/** English strings. A translated content file overrides these through `ui`. */
export const WRITING_PROMPT_GENERATOR_STRINGS = {
  promptCategory: "Prompt category",
  newPrompt: "New Prompt",
  copied: "Copied!",
  copyPrompt: "Copy Prompt",
  recentPrompts: "Recent prompts",
  categoryLabels: {
    creative: "Creative / Fiction",
    narrative: "Narrative / Personal",
    persuasive: "Persuasive / Argumentative",
    descriptive: "Descriptive",
    journal: "Journal / Reflection",
  },
  prompts: {
    creative: [
      "A lighthouse keeper discovers that the light attracts something other than ships.",
      "Write a story where the main character can hear one sentence of every conversation happening in their town.",
      "Two strangers keep meeting in each other's dreams — and one of them starts leaving notes.",
      "Every clock in a small town stops at 3:07 p.m., and only the new mail carrier seems to notice.",
      "Tell the story of a heist from the perspective of the building.",
      "A translator realizes the ancient text they are working on is addressed to them by name.",
      "Write about a world where people trade memories the way we trade money.",
      "The last bookstore on Earth receives a delivery — of a book that has not been written yet.",
      "A child's imaginary friend shows up twenty years later, asking for help.",
      "Write a story that takes place entirely during a power outage.",
      "A cartographer keeps finding a village on their maps that no one else can see.",
      "Every mirror in the house shows the room as it looked exactly one year ago.",
      "A chef discovers their cooking makes people remember things they had forgotten.",
      "Write about the first person ever to hear music — from their point of view.",
      "An astronaut returns from a solo mission to find one small detail about Earth has changed.",
      "A letter arrives sixty years late, and its instructions still work.",
      "Write a story where the villain and the hero have to share an apartment.",
      "The town museum's newest exhibit is a locked door, and last night someone knocked from the other side.",
      "A gardener plants seeds from an unmarked packet and grows something that sings.",
      "Write about the friendship between the last speaker of a dying language and the linguist recording them.",
      "The night ferry across the bay always carries one passenger who never disembarks.",
      "A thief who steals sounds — a laugh, a church bell, a name — decides to give one back.",
      "Write a story told entirely through the objects left behind in a lost-and-found box.",
      "In a city where it never stops raining, one street stays dry — and nobody will say why.",
      "Two siblings inherit a house with one room that rearranges itself overnight.",
    ],
    narrative: [
      "Write about a time you changed your mind about something important.",
      "Describe the moment you realized you were good at something.",
      "Tell the story of a family tradition and what it means to you.",
      "Write about a time you got lost — literally or figuratively — and what you found.",
      "Describe a conversation that stayed with you long after it ended.",
      "Write about a time you had to stand up for someone else.",
      "Tell the story of your name — who chose it, what it means, and how you feel about it.",
      "Write about a small kindness from a stranger that you still remember.",
      "Describe a place from your childhood that no longer exists.",
      "Write about a time you failed at something and what happened next.",
      "Tell the story of the best meal you have ever shared with someone.",
      "Write about a moment when you felt truly proud of yourself.",
      "Describe a time you had to say goodbye before you were ready.",
      "Write about an object you have kept for years and the story attached to it.",
      "Tell the story of a friendship that changed you.",
      "Write about a risk you took that did not pay off — and one that did.",
      "Describe the day you learned something surprising about a family member.",
      "Write about a moment of silence that said more than words could.",
      "Tell the story of a promise you made and what it cost to keep it.",
      "Write about the first time you felt like an adult.",
      "Describe a teacher, coach, or mentor who saw something in you before you did.",
      "Write about a time you were completely wrong about a first impression.",
      "Tell the story of a journey — a trip, a move, a long walk — that changed how you see home.",
      "Write about a habit you inherited from someone you love.",
      "Describe a moment when you chose to forgive — or chose not to.",
    ],
    persuasive: [
      "Should schools replace letter grades with written feedback? Argue your position.",
      "Is social media doing more harm than good to public debate? Defend your answer.",
      "Should college athletes be paid salaries by their universities?",
      "Argue for or against a four-day school or work week.",
      "Should voting be mandatory for all eligible citizens?",
      "Is it ever acceptable for governments to restrict speech online? Defend your view.",
      "Should schools teach a second language starting in elementary school?",
      "Argue whether zoos do more for conservation or more harm to animals.",
      "Should the minimum driving age be raised, lowered, or kept the same?",
      "Is homework an effective learning tool, or should it be eliminated?",
      "Should companies be allowed to use automated systems to screen job applications?",
      "Argue for or against year-round schooling with shorter, more frequent breaks.",
      "Should single-use plastic packaging be banned when alternatives exist?",
      "Is space exploration worth its cost while urgent problems remain on Earth?",
      "Should students be allowed to use phones during the school day? Take a side.",
      "Argue whether professional athletes and celebrities are overpaid.",
      "Should junk food advertising aimed at children be banned?",
      "Is a gap year before college a good idea for most students?",
      "Should public transportation be free in major cities?",
      "Argue for or against requiring community service to graduate from high school.",
      "Should the school day start later to match teenage sleep cycles?",
      "Is it better to specialize early in one field or stay a generalist? Defend your answer.",
      "Should museums return artifacts to the countries they came from?",
      "Argue whether online learning can fully replace in-person classrooms.",
      "Should there be an upper age limit for holding the highest political offices?",
    ],
    descriptive: [
      "Describe a kitchen on a holiday morning using all five senses.",
      "Describe a thunderstorm from indoors — then the same storm from outside.",
      "Describe an empty classroom an hour after the last student leaves.",
      "Describe the oldest building in your town as if it could remember everything it has seen.",
      "Describe a crowded market using only sound and smell.",
      "Describe the feeling of the first truly cold day of autumn.",
      "Describe a library so vividly that a reader could navigate it blindfolded.",
      "Describe your favorite meal being cooked, step by step, sense by sense.",
      "Describe the ocean to someone who has never seen it.",
      "Describe a city street at five in the morning, before the day begins.",
      "Describe a grandparent's hands and everything they have done.",
      "Describe the inside of a car during a long road trip.",
      "Describe snow falling in a place where it almost never snows.",
      "Describe a carnival at night, after the crowds have gone home.",
      "Describe a person's character using only the contents of their bedroom.",
      "Describe the moment just before a race, a performance, or a big test begins.",
      "Describe an old photograph — and the world just outside its frame.",
      "Describe one garden through all four seasons, in four short paragraphs.",
      "Describe a train station where every arrival is a reunion.",
      "Describe the taste of your favorite childhood treat as precisely as you can.",
      "Describe a forest at noon, then the same forest at midnight.",
      "Describe a hospital waiting room without using the word waiting.",
      "Describe a well-worn object — a shoe, a book, a baseball glove — so its history shows.",
      "Describe fog rolling into a harbor town.",
      "Describe your walk to school or work as if you were seeing it for the first time.",
    ],
    journal: [
      "What is something you believe today that you did not believe a year ago?",
      "Write about a fear you have outgrown — or one you are still working on.",
      "What does a perfect ordinary day look like for you? Not a vacation — a Tuesday.",
      "Who in your life do you most want to thank, and what would you say?",
      "Write about a decision you are currently facing, and argue both sides to yourself.",
      "What is a compliment you received that you still think about?",
      "If you could relive one day without changing anything, which day would it be and why?",
      "Write about something you are looking forward to and why it matters.",
      "What is a rule you live by that you have never written down until now?",
      "Write a letter to yourself five years from now.",
      "What is something you do differently from everyone you know?",
      "Write about the last time you felt completely at peace.",
      "What would you attempt if you knew no one would ever judge the result?",
      "Write about a piece of advice you ignored — and whether you regret it.",
      "What are three small things that made this week better?",
      "Write about a version of yourself you have said goodbye to.",
      "What does home mean to you right now, and has that changed?",
      "Write about something you want to learn and what is stopping you.",
      "What is the kindest thing you have done that no one knows about?",
      "Write about a song, book, or film that arrived in your life at exactly the right time.",
      "What do you want more of in your life — and what would you trade for it?",
      "Write about a conversation you need to have but keep postponing.",
      "What did you worry about a year ago that turned out fine?",
      "Write about the person you are becoming, in the present tense.",
      "What would your ten-year-old self find most surprising about your life today?",
    ],
  },
} as const;

export default function WritingPromptGeneratorWidget() {
  const strings = useToolUi(WRITING_PROMPT_GENERATOR_STRINGS);
  const [category, setCategory] = useState<PromptCategory>("creative");
  const [current, setCurrent] = useState("");
  const [recent, setRecent] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  function handleGenerate() {
    const next = pickPrompt(strings.prompts[category], current);
    if (current.length > 0) {
      setRecent((prev) =>
        [current, ...prev.filter((p) => p !== current)].slice(0, MAX_RECENT)
      );
    }
    setCurrent(next);
    setCopied(false);
  }

  function handleCopy() {
    if (current.length === 0) {
      return;
    }
    writeToClipboard(current).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Category */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.promptCategory}
        </p>
        <div className="flex flex-wrap gap-3">
          {CATEGORY_VALUES.map((value) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={value}
            >
              <input
                checked={category === value}
                className="accent-primary"
                name="prompt-category"
                onChange={() => setCategory(value)}
                type="radio"
                value={value}
              />
              {strings.categoryLabels[value]}
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.newPrompt}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={current.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyPrompt}
        </button>
      </div>

      {/* Current prompt */}
      {current.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="text-foreground text-lg leading-relaxed">{current}</p>
          <p className="mt-3 text-muted-foreground text-xs">
            {strings.categoryLabels[category]}
          </p>
        </div>
      )}

      {/* Recent prompts */}
      {recent.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.recentPrompts}
          </h2>
          <ul className="space-y-2">
            {recent.map((p) => (
              <li
                className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-muted-foreground text-sm leading-relaxed"
                key={p}
              >
                {p}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
